import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../Services/translation';
import { searchFacetChange } from '../Actions/FacetedSearch.action';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { FormControlLabel, Checkbox } from '@mui/material';
import classNames from 'classnames';
import { makeStyles } from '@mui/styles';
import styles from './Inputs/InputStyle';

const useStyles = makeStyles(styles);

const FacetedSearchGroup = ({ collapsedFilterList, defaultOpen, group, setCollapsedFilterList }) => {
    const listRef = useRef(null);
    const itemRefs = useRef([]); // Array to store refs for each list item

    const showLessItemCount = 5;

    const [collapsed, setCollapsed] = useStateWithCallbackLazy(true);
    const [collapsedFilter, setCollapsedFilter] = useState(!defaultOpen); // First filter group is default open
    const [maxHeight, setMaxHeight] = useState('auto');

    useEffect(() => {
        if (listRef?.current && group?.options.length > showLessItemCount) {
            calculateListHeight();
        }
    }, [collapsedFilter, collapsed]);

    useEffect(() => {
        // Keep group expanded on filter updates
        collapsedFilterList.includes(group.id) && setCollapsedFilter(false);
    }, [group.options]);

    const calculateListHeight = () => {
        // Calculate total height of the first `showLessItemCount` items dynamically
        const calculatedHeight = itemRefs.current
            .slice(0, showLessItemCount)
            .map((item) => item?.offsetHeight || 0)
            .reduce((total, height) => total + height, 0);

        const fullHeight = listRef?.current?.scrollHeight;

        setMaxHeight(
            group.options.length > showLessItemCount
                ? collapsed
                    ? `${calculatedHeight}px` // Use actual height of first 5 items
                    : `${fullHeight}px`
                : 'auto'
        );
    };

    const toggleFilterGroup = (isCollapsed) => {
        setCollapsedFilter(isCollapsed);

        // Track the expanded/collapsed state of filters
        setCollapsedFilterList((prevList) =>
            prevList.includes(group.id)
                ? prevList.filter((filter) => filter !== group.id)
                : isCollapsed
                  ? [...prevList]
                  : [...prevList, group.id]
        );
    };

    // Show more/less items in filter group
    const toggleShowMore = useCallback(() => {
        setCollapsed((prev) => !prev);
    }, []);

    return (
        <Fragment>
            {group.options.length > 0 && (
                <li
                    className={`faceted-search__group ${collapsedFilter ? 'collapsed' : ''}`}
                    role="group"
                >
                    <button
                        className={`faceted-search__group-header `}
                        onClick={() => toggleFilterGroup(!collapsedFilter)}
                    >
                        <span>{group.label}</span>
                        <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
                    </button>
                    <ul
                        className={`faceted-search__group-list`}
                        style={{
                            maxHeight,
                        }}
                        ref={listRef}
                    >
                        {group.options &&
                            group.options.map((item, itemIndex) => (
                                <li
                                    key={`${item.label}-${itemIndex}`}
                                    className="faceted-search__item"
                                    role="menuitemcheckbox"
                                    ref={(el) => (itemRefs.current[itemIndex] = el)} // Store ref for each item
                                >
                                    <FacetedFilterCheckbox
                                        item={item}
                                        group={group}
                                    />
                                </li>
                            ))}
                    </ul>
                    {group?.options?.length > showLessItemCount && !collapsedFilter && (
                        <button
                            className={`faceted-search__show-more ${collapsed ? 'collapsed-list' : ''}`}
                            onClick={() => toggleShowMore()}
                        >
                            {`${collapsed ? translate('filter.showmore') : translate('filter.showless')}`}
                        </button>
                    )}
                </li>
            )}
        </Fragment>
    );
};

const FacetedFilterCheckbox = ({ item, group }) => {
    const dispatch = useDispatch();
    const onChange = useCallback((group, item) => dispatch(searchFacetChange(group, item)), [dispatch]);

    const classes = useStyles();
    const checkboxClasses = classNames({
        [classes.checkbox]: true,
    });

    return (
        <FormControlLabel
            key={item.id}
            control={
                <Checkbox
                    onChange={() => onChange(group, item)}
                    value={item.value}
                    checked={group.selectedOptions != null && group.selectedOptions.includes(item.id)}
                />
            }
            className={checkboxClasses}
            label={
                <>
                    <span className="faceted-filter__label">{item.label}</span>
                    {!isNaN(item.quantity) && item.quantity != null && (
                        <span className="faceted-filter__quantity">({item.quantity})</span>
                    )}
                </>
            }
        />
    );
};

const FacetedSearch = () => {
    const dispatch = useDispatch();
    const { facetFilters, navigationTheme } = useSelector((state) => state.facetedSearch);
    const { query } = useSelector((state) => state.quickSearch);
    const [collapsedFilterList, setCollapsedFilterList] = useState([]);

    const firstFilterGroupIndex = facetFilters.findIndex((group) => group.options.length > 0);
    const facetFiltersOptions = facetFilters.map((group) => group.options.length > 0);
    const showFacetFilters = facetFiltersOptions.includes(true);

    useEffect(() => {
        window.onpopstate = () => {
            dispatch(query(window.location.search.substr(1) || ''));
        };
    }, [dispatch, query]);

    return (
        facetFilters &&
        facetFilters.length > 0 && (
            <ul className={`faceted-search ${showFacetFilters ? 'show' : ''}`}>
                {facetFilters.map((group, groupIndex) =>
                    !group?.showAsToggle && (
                        <FacetedSearchGroup
                            collapsedFilterList={collapsedFilterList}
                            defaultOpen={groupIndex === firstFilterGroupIndex}
                            group={group}
                            key={`${group.label}-${groupIndex}`}
                            setCollapsedFilterList={setCollapsedFilterList}
                        />
                    )
                )}
            </ul>
        )
    );
};

export default FacetedSearch;
